import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { ApiData } from 'src/app/common/api-response.types';
import { AuthService } from 'src/app/services/auth.service';
import {
  ActiveReservationNotice,
  ReservationsService,
} from 'src/app/services/reservations.service';

const BODY_NOTICE_CLASS = 'has-active-reservation-notice';
const BODY_NOTICE_OFFSET_VAR = '--active-reservation-notice-offset';

@Component({
  selector: 'app-active-reservation-notice',
  templateUrl: './active-reservation-notice.component.html',
  styleUrls: ['./active-reservation-notice.component.scss'],
})
export class ActiveReservationNoticeComponent implements OnInit, OnDestroy {
  notice: ActiveReservationNotice | null = null;
  expiresInLabel = '';
  collapsed = false;
  isToggling = false;

  private countdownSub?: Subscription;
  private loadSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private offsetSyncTimeoutId?: ReturnType<typeof setTimeout>;
  private offsetSyncRetries = 0;

  constructor(
    private readonly reservationsService: ReservationsService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly elementRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.loadNotice();
    this.observeNoticeSize();
  }

  ngOnDestroy(): void {
    this.countdownSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    if (this.offsetSyncTimeoutId !== undefined) {
      clearTimeout(this.offsetSyncTimeoutId);
    }
    this.clearBodyOffset();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.scheduleBodyOffsetSync();
  }

  viewQr(): void {
    if (!this.notice?.id) {
      return;
    }
    void this.router.navigate(['/reservations', this.notice.id]);
  }

  collapse(): void {
    this.setCollapsed(true);
  }

  expand(): void {
    this.setCollapsed(false);
  }

  private setCollapsed(collapsed: boolean): void {
    if (!this.notice?.id || this.isToggling || this.collapsed === collapsed) {
      return;
    }

    this.isToggling = true;
    this.applyCollapsed(collapsed);
    const request$ = collapsed
      ? this.reservationsService.dismissActiveNotice(this.notice.id)
      : this.reservationsService.restoreActiveNotice(this.notice.id);

    request$.subscribe({
      next: () => {
        this.isToggling = false;
      },
      error: () => {
        this.applyCollapsed(!collapsed);
        this.isToggling = false;
      },
    });
  }

  private applyCollapsed(collapsed: boolean): void {
    this.collapsed = collapsed;
    this.reservationsService.setActiveNoticeCollapsed(collapsed);
    this.scheduleBodyOffsetSync();
  }

  private loadNotice(): void {
    if (!this.authService.isAuthenticated()) {
      this.clearNotice();
      return;
    }

    this.loadSub?.unsubscribe();
    this.loadSub = this.reservationsService.getActiveNotice().subscribe({
      next: (response) => {
        const data = this.unwrapNotice(response?.data);
        if (!data?.id) {
          this.clearNotice();
          return;
        }

        if (data.expiryDate && this.isExpired(data.expiryDate)) {
          this.clearNotice();
          return;
        }

        this.notice = data;
        this.collapsed = data.collapsed === true;
        this.reservationsService.setActiveNoticeCollapsed(this.collapsed);
        this.refreshExpiresLabel();
        if (data.expiryDate) {
          this.startCountdown();
        }
        this.scheduleBodyOffsetSync();
      },
      error: () => {
        this.clearNotice();
      },
    });
  }

  private startCountdown(): void {
    this.countdownSub?.unsubscribe();
    this.countdownSub = interval(30_000).subscribe(() => {
      this.refreshExpiresLabel();
    });
  }

  private refreshExpiresLabel(): void {
    if (!this.notice?.expiryDate) {
      this.expiresInLabel = '';
      return;
    }

    if (this.isExpired(this.notice.expiryDate)) {
      this.clearNotice();
      return;
    }

    this.expiresInLabel = this.formatRemaining(this.notice.expiryDate);
  }

  private isExpired(expiryDate: string): boolean {
    const expiryMs = new Date(expiryDate).getTime();
    if (!Number.isFinite(expiryMs)) {
      return true;
    }
    return expiryMs <= Date.now();
  }

  private formatRemaining(expiryDate: string): string {
    const remainingMs = Math.max(
      0,
      new Date(expiryDate).getTime() - Date.now(),
    );
    const totalMinutes = Math.floor(remainingMs / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hoursLabel = String(hours).padStart(2, '0');
    const minutesLabel = String(minutes).padStart(2, '0');
    return `${hoursLabel}h ${minutesLabel} m`;
  }

  private clearNotice(): void {
    this.notice = null;
    this.expiresInLabel = '';
    this.collapsed = false;
    this.reservationsService.setActiveNoticeCollapsed(false);
    this.offsetSyncRetries = 0;
    this.countdownSub?.unsubscribe();
    this.clearBodyOffset();
  }

  private observeNoticeSize(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleBodyOffsetSync();
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  private scheduleBodyOffsetSync(): void {
    if (this.offsetSyncTimeoutId !== undefined) {
      clearTimeout(this.offsetSyncTimeoutId);
    }
    this.offsetSyncTimeoutId = setTimeout(() => this.syncBodyOffset());
  }

  private syncBodyOffset(): void {
    if (!this.notice) {
      this.offsetSyncRetries = 0;
      this.clearBodyOffset();
      return;
    }

    const noticeEl =
      this.elementRef.nativeElement.querySelector<HTMLElement>(
        '.active-reservation-notice',
      ) ?? this.elementRef.nativeElement;
    const styles = getComputedStyle(noticeEl);
    const marginBottom = Number.parseFloat(styles.marginBottom) || 0;
    const height = Math.ceil(
      noticeEl.getBoundingClientRect().height + marginBottom,
    );

    if (height <= 0) {
      if (this.offsetSyncRetries < 10) {
        this.offsetSyncRetries += 1;
        this.scheduleBodyOffsetSync();
      }
      return;
    }

    this.offsetSyncRetries = 0;
    document.body.classList.add(BODY_NOTICE_CLASS);
    document.body.style.setProperty(BODY_NOTICE_OFFSET_VAR, `${height}px`);
  }

  private clearBodyOffset(): void {
    document.body.classList.remove(BODY_NOTICE_CLASS);
    document.body.style.removeProperty(BODY_NOTICE_OFFSET_VAR);
  }

  private unwrapNotice(
    data: ApiData<ActiveReservationNotice | null> | undefined,
  ): ActiveReservationNotice | null {
    if (!data || Array.isArray(data) || !('id' in data)) {
      return null;
    }
    return data;
  }
}
